import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { useRef, useState } from 'react';
import AvatarChip from '../../../../../shared/avatar-chip.tsx';
import { DecomModel } from '../../../../../../types';
import SharedAutocomplete from '../../../../../shared/shared-autocomplete.tsx';
import DecomOrgAutoComplete from '../../decom-policy-grid-actions/decom-org-autocomplete.tsx';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { FormSectionHeader } from '../../../../../shared/form-section-header.tsx';
import SharedFormCheckbox from '../../../../../shared/shared-form-checkbox.tsx';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';

interface BasicInfoStepProps {
  control: Control<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  decomModels: DecomModel[];
}

export default function BasicInfoStep({
  control,
  watch,
  setValue,
  errors,
  trigger,
  clearErrors,
  decomModels,
}: BasicInfoStepProps) {
  const autocompleteKeyOrgs = useRef(new Date().getTime().toString());
  const [autoCompleteResponseOrgs, setAutoCompleteResponseOrgs] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const orgs = watch('org') || [];
  const notificationUser = watch('notificationUser') || '';
  const triggerType = watch('triggerType') || 'suspension';
  const selectedActions =
    Object.values(watch('actions') ?? {})
      .flatMap(
        (day: { actions?: Array<{ actionType: string }> }) => day.actions ?? []
      )
      .map((a: { actionType: string }) => a.actionType) ?? [];

  const actionsData = watch('actions') || [];

  const policyTypeOptions = [
    { displayValue: 'User Suspension', value: 'suspension' },
    { displayValue: 'Org Unit Change', value: 'move' },
  ];

  function removeAction(actionType: string) {
    const updatedActions = Object.fromEntries(
      Object.entries(actionsData).map(([day, dayData]) => {
        const filteredActions = dayData.actions.filter(
          (a) => a.actionType !== actionType
        );
        return [
          day,
          {
            ...dayData,
            actions: filteredActions,
          },
        ];
      })
    );

    setValue('actions', updatedActions, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const handleOrgAdd = (newValue: string) => {
    if (!newValue) return;

    const existing = orgs || [];
    const alreadyExists = existing.some(
      (org) => org.toLowerCase() === newValue.toLowerCase()
    );

    if (!alreadyExists) {
      const updated = [...existing, newValue];
      setValue('org', updated, { shouldDirty: true });
      trigger('org');
    }

    setAutoCompleteResponseOrgs('');
    autocompleteKeyOrgs.current = new Date().getTime().toString();
  };

  const handleOrgDelete = (org: string) => {
    setValue(
      'org',
      orgs.filter((orgUnit) => orgUnit !== org),
      { shouldDirty: true }
    );
    trigger('org');
  };

  const handleNotificationUserDelete = () => {
    setValue('notificationUser', '', { shouldDirty: true });
    setValue('sendSummary', false, { shouldDirty: true });
  };

  const handleNotificationUserAdd = (newVal: string) => {
    setValue('notificationUser', newVal, {
      shouldDirty: true,
    });
    setValue('sendSummary', true, {
      shouldDirty: true,
    });
    trigger('notificationUser');
  };

  return (
    <Stack spacing={4} width="100%">
      <Stack spacing={1} sx={{ flex: 1, pr: 3 }}>
        <Stack spacing={0.9}>
          <Stack spacing={2}>
            <FormSectionHeader>Basic Information</FormSectionHeader>
            <Stack spacing={0.9}>
              {/* Policy Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Policy Name"
                    required
                    fullWidth
                    {...field}
                    onBlur={(e) => {
                      field.onChange(e.target.value.trim());
                      field.onBlur();
                      if (field.value !== '') clearErrors('name');
                    }}
                    error={!!errors.name}
                    helperText={errors.name?.message ?? ' '}
                    autoComplete="off"
                    sx={{ maxWidth: 400 }}
                  />
                )}
              />
            </Stack>
          </Stack>
          {/* Trigger Type + Act Immediately */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name="triggerType"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label="Trigger Type"
                  fullWidth
                  value={field.value || ''}
                  helperText={' '}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    setValue('validateUserIsStillSuspended', false, {
                      shouldDirty: true,
                    });
                  }}
                  required
                  sx={{ flex: 1, maxWidth: 400 }}
                >
                  {policyTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <SharedFormCheckbox
            control={control}
            name="actImmediately"
            label="Act Immediately"
            helperText="The decommissioning process is kicked off immediately based on the trigger type, rather than being evaluated on a nightly basis."
          />

          {/* Select Org */}
          <Stack sx={{ pb: 3 }}>
            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Select Organizations to apply this Decom Policy to:
              </Typography>
            </Box>
            <Stack flexDirection="row" gap={1}>
              <Box sx={{ width: '400px' }}>
                <Controller
                  name={'org'}
                  control={control}
                  render={(field) => (
                    <DecomOrgAutoComplete
                      {...field}
                      newKey={autocompleteKeyOrgs.current}
                      autoCompleteValue={autoCompleteResponseOrgs}
                      setAutoCompleteValue={handleOrgAdd}
                      hasError={!!errors.org}
                      helperText={errors.org?.message ?? ''}
                      decomModels={decomModels}
                    />
                  )}
                />
              </Box>
            </Stack>

            {orgs.length > 0 && (
              <Stack
                flexDirection="row"
                gap={1}
                flexWrap="wrap"
                sx={{ mt: 2, ml: 1 }}
              >
                {orgs.map((org) => (
                  <AvatarChip
                    key={org}
                    label={org}
                    type={'OrgUnit'}
                    email={org}
                    onDelete={() => handleOrgDelete(org)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ width: '100%' }} />
        {/* More Configuration Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', py: 2 }}>
          <Link
            component="button"
            type="button"
            onClick={() => setShowAdvanced((prev) => !prev)}
            underline="always"
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: (theme) => theme.palette.semantic.hyperlink.active,
              cursor: 'pointer',
            }}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Configuration
          </Link>
        </Box>

        <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {/* Summary Email */}
            <Stack sx={{ mb: 5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Decommission Run Notification Email
              </Typography>
              <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                {notificationUser ? (
                  <AvatarChip
                    type="Unknown"
                    email={notificationUser}
                    onDelete={handleNotificationUserDelete}
                    size="small"
                  />
                ) : (
                  <Controller
                    name="notificationUser"
                    control={control}
                    render={() => (
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ width: '400px', mb: 1 }}>
                          <SharedAutocomplete
                            sources={['users', 'groups']}
                            autocompleteValue={notificationUser}
                            setAutoCompleteValue={handleNotificationUserAdd}
                          />
                        </Box>
                        <FormHelperText sx={{ pl: 1 }}>
                          This email will show all actions to be taken for the
                          day's decommissioning run. It will be sent at the
                          start of each day's run.
                        </FormHelperText>
                      </Box>
                    )}
                  />
                )}
              </Stack>
            </Stack>

            {/* Leave Unsuspended */}
            <SharedFormCheckbox
              control={control}
              name="keepUnsuspended"
              label="Leave Unsuspended"
              helperText="If no decommissioning action changed the user status (e.g., Suspend User, Unsuspend User, Archive User), then at the end of each decommissioning run, the user will be set to Unsuspended."
            />

            {/* Pause On User Org Change */}
            <SharedFormCheckbox
              control={control}
              name="validateUserIsStillInDecomOrg"
              label="Pause Decom On User Org Change"
              helperText="Selecting this option will remove the User Org Change action from this policy if it has been selected."
              onChange={(checked) => {
                if (checked && selectedActions.includes('UserOrgChange')) {
                  removeAction('UserOrgChange');
                }
              }}
            />

            {/* Pause On Unsuspension (if triggerType is 'suspension') */}
            {triggerType === 'suspension' && (
              <SharedFormCheckbox
                control={control}
                name="validateUserIsStillSuspended"
                label="Pause Decom On Unsuspension"
                helperText="Selecting this option will remove the Unsuspended User action from this policy if it has been selected."
                onChange={(checked) => {
                  if (checked && selectedActions.includes('unsuspendUser')) {
                    removeAction('unsuspendUser');
                  }
                }}
              />
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
}
